import React, { useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { isEmpty } from 'lodash';
import makeStyles from '@mui/styles/makeStyles';
import BaseModalTitle from '@monorepo/shared/src/components/common/Modals/BaseModalTitle';
import { useModal } from '@monorepo/shared/node_modules/@lwtears/lwt-common-frontend/lib/Modal';
import { tA11y } from '@monorepo/shared/node_modules/@lwtears/lwt-common-frontend/lib/@common/util/i18n-util';
import { getLiteSession } from '@monorepo/shared/src/api/lite-session';
import useTelemetry from '@monorepo/shared/src/hooks/use-telemetry';
import { FONT_GSS_FUTURA } from '@monorepo/shared/src/themes/defaultTheme';
import { TelemetryEvents } from '@monorepo/shared/src/types';
import { remCalc } from '@monorepo/shared/src/util/style-util';
import ResourceCard from './ResourceCard';
import ResourcePlayer from './ResourcePlayer';
import { FavoriteResource, Resource } from '../../../providers/ResourcesProvider/types';
import { AppNames, ResourceTypes } from '../../../types';
import { sortResourceByTitle } from '../../../util/resources-util';
import { ApplicationSection } from '../../../graph/schema-generated-types-educator';
import { formatCharacter } from '../../../util/telemetry-util';

type Props = {
  resources: Resource[];
  pinnedResources?: FavoriteResource[];
  areDisplayingCardHeaders?: boolean;
  areDisplayingPinnedResources?: boolean;
  areDisplayingPinToggle?: boolean;
  onPinClick?: (resource: Resource, pinnedResourceId: number | undefined) => void;
};

const ResourcesList: React.FC<Props> = ({
  resources,
  pinnedResources = [],
  areDisplayingCardHeaders = true,
  areDisplayingPinnedResources = false,
  areDisplayingPinToggle = true,
  onPinClick,
}) => {
  const [, { fireEvent }] = useTelemetry(TelemetryEvents.ACCESS_RESOURCE);
  const classes = useStyles();
  const [, { openModal }] = useModal();
  const location = useLocation();

  const handleIconButtonClick = useCallback(
    (resource: Resource, pinnedResourceId: number | undefined) => {
      onPinClick?.(resource, pinnedResourceId);
    },
    [onPinClick]
  );

  const createTelemetryEvent = async (resource) => {
    const isLetterLearning = location.pathname.includes('letter-teaching');
    const section = isLetterLearning
      ? ApplicationSection.LETTER_LEARNING
      : ApplicationSection.RESOURCES;

    if (!resource) return;
    const { id, type, characters } = resource;
    const character = { ...characters[0] };
    delete character.__typename;

    fireEvent({
      resourceId: id,
      resourceType: type.type,
      resourceTitle: resource.title,
      section,
      ...(character && !isEmpty(character) && { character: formatCharacter(character) }),
    });
  };

  const handleResourceCardClick = useCallback(
    async (resource: Resource) => {
      await createTelemetryEvent(resource);
      let resourcePlayerUrl = '';

      if (
        [ResourceTypes.DID_YOU_KNOW, ResourceTypes.WALL_CARD, ResourceTypes.WDT].includes(
          resource.type.type as ResourceTypes
        )
      ) {
        const appName = AppNames.GSS;
        const { sessionId } = await getLiteSession(appName);
        resourcePlayerUrl = `${resource.url}&session=${sessionId}`;
      }

      openModal({
        Title: () => (
          <BaseModalTitle text={resource.title} className={classes.fullScreenModalTitle} />
        ),
        body: (
          <ResourcePlayer
            selectedResource={{
              data: resource.data,
              url: resourcePlayerUrl,
              title: resource.title,
              mediaType: resource.asset?.type,
              aspectRatio: (resource.data as any)?.aspectRatio,
              heightAdjust: (resource.data as any)?.heightAdjust,
              type: resource.type,
              src: resource.asset?.src,
              vtt: resource.asset?.vtt as string,
              narration: {
                src: resource.asset?.narrationSrc as string,
                vtt: resource.asset?.narrationVtt as string,
              },
            }}
            loadedData={() => null}
            fullScreen
          />
        ),
        ariaLabel: tA11y('aria.modal', { title: resource.title }),
        options: { closeOnBackdropClick: true },
        getContainerStyles: (defaultStyles) => ({ ...defaultStyles, ...{ maxWidth: '100vw' } }),
        getPaperStyles: (defaultStyles) => ({ ...defaultStyles, ...{ maxWidth: '100vw' } }),
      });
    },
    [openModal]
  );

  return (
    <div className={classes.root}>
      <ul className={`${classes.resourcesList} ${classes.gridLayout}`}>
        {sortResourceByTitle(resources).map((resource: Resource) => {
          const pinnedResource = pinnedResources.find(
            (pinnedResource: FavoriteResource) => pinnedResource.resource.id === resource.id
          );

          return (
            <li key={`${resource.title}`}>
              <ResourceCard
                resource={resource}
                isPinnedResource={areDisplayingPinnedResources ? null : !!pinnedResource}
                pinnedResourceId={pinnedResource?.id}
                displayCardHeader={areDisplayingCardHeaders}
                displayPinToggle={areDisplayingPinToggle}
                onCardClick={handleResourceCardClick}
                onIconButtonClick={handleIconButtonClick}
              />
            </li>
          );
        })}
      </ul>
    </div>
  );
};

const screenSize = {
  smartphone: '@media only screen and (max-width: 499px)',
  smartphoneLandscape: '@media only screen and (min-width: 500px) and (max-width: 799px)',
  tablet: '@media only screen and (min-width: 800px) and (max-width: 1199px)',
  desktop: '@media only screen and (min-width: 1200px) and (max-width: 1699px)',
  wideScreen: '@media only screen and (min-width: 1700px)',
};

const useStyles = makeStyles(() => ({
  avatar: {
    display: 'flex',
    height: remCalc(32),
    justifyContent: 'center',
    marginLeft: remCalc(3),
    width: remCalc(32),
  },
  fullScreenModalTitle: {
    alignItems: 'center',
    zIndex: 1,
    height: '60px',
    maxHeight: '60px',
    boxShadow:
      '0px 2px 4px -1px rgb(0 0 0 / 20%), 0px 4px 5px 0px rgb(0 0 0 / 14%), 0px 1px 10px 0px rgb(0 0 0 / 12%)',
    '& h2': {
      fontWeight: 'bold',
    },
  },
  gridLayout: {
    display: 'grid',
    gridAutoFlow: 'row',
    gridGap: '30px 30px',
    justifyContent: 'center',
    listStyle: 'none',
    [screenSize.smartphone]: {
      gridTemplateColumns: '1fr',
    },
    [screenSize.smartphoneLandscape]: {
      gridTemplateColumns: '1fr',
    },
    [screenSize.tablet]: {
      gridTemplateColumns: '1fr 1fr',
    },
    [screenSize.desktop]: {
      gridTemplateColumns: '1fr 1fr 1fr',
    },
    [screenSize.wideScreen]: {
      gridTemplateColumns: '1fr 1fr 1fr 1fr',
    },
    width: '100%',
  },
  resourcesList: {
    fontFamily: FONT_GSS_FUTURA,
    padding: `0px ${remCalc(19.2)}`,
  },
  root: {
    display: 'flex',
    marginTop: remCalc(19.2),
    marginBottom: remCalc(19.2),
  },
}));

export default ResourcesList;
